import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from './api';
import Spinner from './Spinner';

function asArray(data) {
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.results)) return data.results;
    return [];
}

function formatError(err) {
    const data = err?.response?.data;
    if (!data) return err?.message || 'Request failed';
    if (typeof data === 'string') return data;
    if (data.detail) return data.detail;
    if (data.error) return data.error;
    const parts = Object.entries(data).map(([key, value]) => {
        const msg = Array.isArray(value) ? value.join(' ') : String(value);
        return `${key}: ${msg}`;
    });
    return parts.join(' | ') || 'Request failed';
}

function toLocalInputValue(date = new Date()) {
    const pad = (n) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function formatDateTime(value) {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString();
}

const emptyForm = () => ({
    employee: '',
    leave_type: '',
    start_datetime: toLocalInputValue(),
    end_datetime: toLocalInputValue(new Date(Date.now() + 8 * 60 * 60 * 1000)),
    total_hours: '8',
    operational_remarks: '',
});

function getStyles(theme) {
    const isDark = theme === 'dark';
    return {
        hint: { color: isDark ? '#9aa6b2' : '#6c757d', fontSize: '13px', margin: '0 0 14px' },
        filters: {
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            alignItems: 'end',
            marginBottom: '14px',
        },
        formGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
            gap: '12px',
            alignItems: 'end',
        },
        formGroup: { display: 'flex', flexDirection: 'column', gap: '4px' },
        label: { fontWeight: 600, fontSize: '13px' },
        input: {
            padding: '10px',
            borderRadius: '4px',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            backgroundColor: isDark ? '#2a2a2a' : '#fff',
            color: isDark ? '#e0e0e0' : '#212529',
            fontSize: '14px',
        },
        buttonRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '12px' },
        button: {
            padding: '10px 14px',
            backgroundColor: isDark ? '#6cb2f7' : '#0d6efd',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        secondaryButton: {
            padding: '10px 14px',
            backgroundColor: 'transparent',
            color: isDark ? '#e0e0e0' : '#212529',
            border: `1px solid ${isDark ? '#444' : '#dee2e6'}`,
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 600,
            fontSize: '14px',
        },
        successButton: {
            padding: '6px 10px',
            backgroundColor: isDark ? '#2e7d32' : '#198754',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            marginRight: '6px',
        },
        dangerButton: {
            padding: '6px 10px',
            backgroundColor: '#dc3545',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            marginRight: '6px',
        },
        warnButton: {
            padding: '6px 10px',
            backgroundColor: isDark ? '#c9a227' : '#ffc107',
            color: isDark ? '#111' : '#212529',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '13px',
            marginRight: '6px',
        },
        section: {
            backgroundColor: isDark ? '#1a1a1a' : '#f8f9fa',
            border: `1px solid ${isDark ? '#333' : '#dee2e6'}`,
            borderRadius: '8px',
            padding: '16px',
            marginBottom: '16px',
        },
        sectionTitle: { margin: '0 0 12px', fontSize: '16px' },
        tableWrap: { overflowX: 'auto' },
        table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
        th: {
            textAlign: 'left',
            padding: '10px 8px',
            borderBottom: `2px solid ${isDark ? '#444' : '#dee2e6'}`,
            whiteSpace: 'nowrap',
        },
        td: {
            padding: '10px 8px',
            borderBottom: `1px solid ${isDark ? '#333' : '#dee2e6'}`,
            verticalAlign: 'top',
        },
        badge: {
            display: 'inline-block',
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '12px',
            fontWeight: 700,
            backgroundColor: isDark ? '#333' : '#e9ecef',
        },
        pendingBadge: {
            backgroundColor: isDark ? '#5c4a12' : '#fff3cd',
            color: isDark ? '#ffe8a3' : '#664d03',
        },
        approvedBadge: {
            backgroundColor: isDark ? '#1b4332' : '#d1e7dd',
            color: isDark ? '#b7f0c8' : '#0f5132',
        },
    };
}

function statusBadgeStyle(status, styles) {
    if (status === 'Submitted' || status === 'Pending Second Approval') {
        return { ...styles.badge, ...styles.pendingBadge };
    }
    if (status === 'Approved') {
        return { ...styles.badge, ...styles.approvedBadge };
    }
    return styles.badge;
}

function ManningLeavePanel({ theme, onNotify, onChanged }) {
    const styles = useMemo(() => getStyles(theme), [theme]);
    const [leaveTypes, setLeaveTypes] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [requests, setRequests] = useState([]);
    const [pending, setPending] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState(null);
    const [reviewComments, setReviewComments] = useState({});
    const [filters, setFilters] = useState({ status: '', search: '' });

    const activeEmployees = useMemo(
        () =>
            employees.filter((e) =>
                ['Active', 'On Extended LOA', 'Pending Start'].includes(e.status)
            ),
        [employees]
    );

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.search) params.search = filters.search;
            const [typesRes, empRes, listRes, pendingRes] = await Promise.all([
                api.get('/leave-types/', { params: { is_active: true } }),
                api.get('/employees/'),
                api.get('/leave-requests/', { params }),
                api.get('/leave-requests/', { params: { pending: true } }),
            ]);
            setLeaveTypes(asArray(typesRes.data));
            setEmployees(asArray(empRes.data));
            setRequests(asArray(listRes.data));
            setPending(asArray(pendingRes.data));
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setLoading(false);
        }
    }, [filters, onNotify]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const resetForm = () => {
        setForm(emptyForm());
        setEditingId(null);
    };

    const saveDraft = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                employee: Number(form.employee),
                leave_type: Number(form.leave_type),
                start_datetime: new Date(form.start_datetime).toISOString(),
                end_datetime: new Date(form.end_datetime).toISOString(),
                total_hours: form.total_hours,
                operational_remarks: form.operational_remarks || '',
            };
            if (editingId) {
                await api.patch(`/leave-requests/${editingId}/`, payload);
                onNotify?.('Leave chit updated');
            } else {
                await api.post('/leave-requests/', payload);
                onNotify?.('Leave chit saved as draft');
            }
            resetForm();
            await loadData();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const saveAndSubmit = async (event) => {
        event.preventDefault();
        setSaving(true);
        try {
            const payload = {
                employee: Number(form.employee),
                leave_type: Number(form.leave_type),
                start_datetime: new Date(form.start_datetime).toISOString(),
                end_datetime: new Date(form.end_datetime).toISOString(),
                total_hours: form.total_hours,
                operational_remarks: form.operational_remarks || '',
            };
            let id = editingId;
            if (editingId) {
                await api.patch(`/leave-requests/${editingId}/`, payload);
            } else {
                const created = await api.post('/leave-requests/', payload);
                id = created.data.id;
            }
            await api.post(`/leave-requests/${id}/submit/`);
            onNotify?.('Leave chit submitted for approval');
            resetForm();
            await loadData();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (item) => {
        if (!['Draft', 'Returned for Correction'].includes(item.status)) {
            onNotify?.('Only draft or returned chits can be edited', 'error');
            return;
        }
        setEditingId(item.id);
        setForm({
            employee: String(item.employee),
            leave_type: String(item.leave_type),
            start_datetime: toLocalInputValue(new Date(item.start_datetime)),
            end_datetime: toLocalInputValue(new Date(item.end_datetime)),
            total_hours: String(item.total_hours),
            operational_remarks: item.operational_remarks || '',
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const runAction = async (id, actionName) => {
        setSaving(true);
        try {
            const comments = reviewComments[id] || '';
            await api.post(`/leave-requests/${id}/${actionName}/`, { comments });
            onNotify?.(`Leave chit ${actionName.replace(/_/g, ' ')} complete`);
            await loadData();
            onChanged?.();
        } catch (err) {
            onNotify?.(formatError(err), 'error');
        } finally {
            setSaving(false);
        }
    };

    if (loading && requests.length === 0) {
        return (
            <div style={{ padding: '24px', textAlign: 'center' }}>
                <Spinner />
            </div>
        );
    }

    return (
        <div>
            <p style={styles.hint}>
                Supervisors enter leave chits for employees. Submit for approval; approved leave
                creates an absence event and updates the org chart badge. Long LOA types may require
                a second staff approval.
            </p>

            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                    {editingId ? 'Edit leave chit' : 'Enter leave chit'}
                </h3>
                <form>
                    <div style={styles.formGrid}>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Employee</label>
                            <select
                                style={styles.input}
                                required
                                value={form.employee}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, employee: e.target.value }))
                                }
                            >
                                <option value="">Select employee</option>
                                {activeEmployees.map((emp) => (
                                    <option key={emp.id} value={emp.id}>
                                        {emp.display_name} ({emp.employee_number})
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Leave type</label>
                            <select
                                style={styles.input}
                                required
                                value={form.leave_type}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, leave_type: e.target.value }))
                                }
                            >
                                <option value="">Select type</option>
                                {leaveTypes.map((lt) => (
                                    <option key={lt.id} value={lt.id}>
                                        {lt.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Start</label>
                            <input
                                style={styles.input}
                                type="datetime-local"
                                required
                                value={form.start_datetime}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        start_datetime: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>End</label>
                            <input
                                style={styles.input}
                                type="datetime-local"
                                required
                                value={form.end_datetime}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        end_datetime: e.target.value,
                                    }))
                                }
                            />
                        </div>
                        <div style={styles.formGroup}>
                            <label style={styles.label}>Total hours</label>
                            <input
                                style={styles.input}
                                type="number"
                                min="0.25"
                                step="0.25"
                                required
                                value={form.total_hours}
                                onChange={(e) =>
                                    setForm((prev) => ({ ...prev, total_hours: e.target.value }))
                                }
                            />
                        </div>
                        <div style={{ ...styles.formGroup, gridColumn: '1 / -1' }}>
                            <label style={styles.label}>Operational remarks</label>
                            <textarea
                                style={{ ...styles.input, minHeight: '70px' }}
                                value={form.operational_remarks}
                                onChange={(e) =>
                                    setForm((prev) => ({
                                        ...prev,
                                        operational_remarks: e.target.value,
                                    }))
                                }
                                placeholder="Coverage notes only — avoid unnecessary personal details"
                            />
                        </div>
                    </div>
                    <div style={styles.buttonRow}>
                        <button
                            type="button"
                            style={styles.secondaryButton}
                            disabled={saving}
                            onClick={saveDraft}
                        >
                            {saving ? 'Saving…' : 'Save draft'}
                        </button>
                        <button
                            type="button"
                            style={styles.button}
                            disabled={saving}
                            onClick={saveAndSubmit}
                        >
                            {saving ? 'Saving…' : 'Save & submit'}
                        </button>
                        {editingId && (
                            <button type="button" style={styles.secondaryButton} onClick={resetForm}>
                                Cancel edit
                            </button>
                        )}
                    </div>
                </form>
            </div>

            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>
                    Pending approvals ({pending.length})
                </h3>
                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Request</th>
                                <th style={styles.th}>Employee</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Window</th>
                                <th style={styles.th}>Hours</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Comments / actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pending.length === 0 ? (
                                <tr>
                                    <td style={styles.td} colSpan={7}>
                                        No leave chits awaiting approval.
                                    </td>
                                </tr>
                            ) : (
                                pending.map((item) => (
                                    <tr key={item.id}>
                                        <td style={styles.td}>{item.request_number}</td>
                                        <td style={styles.td}>
                                            {item.employee_name}
                                            <div style={{ fontSize: '12px', opacity: 0.75 }}>
                                                #{item.employee_number}
                                            </div>
                                        </td>
                                        <td style={styles.td}>{item.leave_type_name}</td>
                                        <td style={styles.td}>
                                            {formatDateTime(item.start_datetime)}
                                            <br />
                                            {formatDateTime(item.end_datetime)}
                                        </td>
                                        <td style={styles.td}>{item.total_hours}</td>
                                        <td style={styles.td}>
                                            <span style={statusBadgeStyle(item.status, styles)}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            <input
                                                style={{ ...styles.input, marginBottom: '8px' }}
                                                placeholder="Review comments"
                                                value={reviewComments[item.id] || ''}
                                                onChange={(e) =>
                                                    setReviewComments((prev) => ({
                                                        ...prev,
                                                        [item.id]: e.target.value,
                                                    }))
                                                }
                                            />
                                            {item.can_approve && (
                                                <button
                                                    type="button"
                                                    style={styles.successButton}
                                                    disabled={saving}
                                                    onClick={() => runAction(item.id, 'approve')}
                                                >
                                                    Approve
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                style={styles.dangerButton}
                                                disabled={saving}
                                                onClick={() => runAction(item.id, 'deny')}
                                            >
                                                Deny
                                            </button>
                                            <button
                                                type="button"
                                                style={styles.warnButton}
                                                disabled={saving}
                                                onClick={() =>
                                                    runAction(item.id, 'return_for_correction')
                                                }
                                            >
                                                Return
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <div style={styles.section}>
                <h3 style={styles.sectionTitle}>All leave chits</h3>
                <div style={styles.filters}>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Status</label>
                        <select
                            style={styles.input}
                            value={filters.status}
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, status: e.target.value }))
                            }
                        >
                            <option value="">All</option>
                            {[
                                'Draft',
                                'Submitted',
                                'Pending Second Approval',
                                'Approved',
                                'Denied',
                                'Canceled',
                                'Returned for Correction',
                            ].map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div style={styles.formGroup}>
                        <label style={styles.label}>Search</label>
                        <input
                            style={styles.input}
                            value={filters.search}
                            placeholder="Request #, name…"
                            onChange={(e) =>
                                setFilters((prev) => ({ ...prev, search: e.target.value }))
                            }
                        />
                    </div>
                    <button type="button" style={styles.button} onClick={loadData}>
                        Apply
                    </button>
                </div>
                <div style={styles.tableWrap}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Request</th>
                                <th style={styles.th}>Employee</th>
                                <th style={styles.th}>Type</th>
                                <th style={styles.th}>Window</th>
                                <th style={styles.th}>Hours</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Remarks</th>
                                <th style={styles.th}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {requests.length === 0 ? (
                                <tr>
                                    <td style={styles.td} colSpan={8}>
                                        No leave chits found.
                                    </td>
                                </tr>
                            ) : (
                                requests.map((item) => (
                                    <tr key={item.id}>
                                        <td style={styles.td}>{item.request_number}</td>
                                        <td style={styles.td}>{item.employee_name}</td>
                                        <td style={styles.td}>{item.leave_type_name}</td>
                                        <td style={styles.td}>
                                            {formatDateTime(item.start_datetime)}
                                            <br />
                                            {formatDateTime(item.end_datetime)}
                                        </td>
                                        <td style={styles.td}>{item.total_hours}</td>
                                        <td style={styles.td}>
                                            <span style={statusBadgeStyle(item.status, styles)}>
                                                {item.status}
                                            </span>
                                        </td>
                                        <td style={styles.td}>
                                            {item.operational_remarks || '—'}
                                            {item.review_comments ? (
                                                <div style={{ fontSize: '12px', opacity: 0.8 }}>
                                                    Review: {item.review_comments}
                                                </div>
                                            ) : null}
                                        </td>
                                        <td style={styles.td}>
                                            {['Draft', 'Returned for Correction'].includes(
                                                item.status
                                            ) && (
                                                <>
                                                    <button
                                                        type="button"
                                                        style={styles.successButton}
                                                        disabled={saving}
                                                        onClick={() => startEdit(item)}
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        style={styles.button}
                                                        disabled={saving}
                                                        onClick={() => runAction(item.id, 'submit')}
                                                    >
                                                        Submit
                                                    </button>
                                                </>
                                            )}
                                            {!['Denied', 'Canceled'].includes(item.status) && (
                                                <button
                                                    type="button"
                                                    style={styles.dangerButton}
                                                    disabled={saving}
                                                    onClick={() => runAction(item.id, 'cancel')}
                                                >
                                                    Cancel
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

export default ManningLeavePanel;
